/**
 * 🟥 CUADRO NEGRO — WorkspaceContext
 *
 * Estado global compartido entre Workspace y Builder.
 * - `deviceMode` (desktop | tablet | cell) para simular el ancho del sandbox.
 * - `executeAction(payload)`: canal estructurado Chat → Workspace del
 *   Cuadro Negro (ver `/api/workspace/execute-action`). Permite ejecutar
 *   `create`/`update` de notas, documentos, tareas y proyectos desde la
 *   conversación y refrescar el Workspace en tiempo real.
 */
import { createContext, useCallback, useContext, useState } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../config/api';

const WorkspaceContext = createContext(null);
const API = `${BACKEND_URL}/api`;

export const WorkspaceProvider = ({ children }) => {
  const [deviceMode, setDeviceMode] = useState('desktop');

  // Handler exacto del Cuadro Negro anterior: máquina de estados cerrada.
  const handleDeviceSwitch = useCallback(() => {
    setDeviceMode((prev) => {
      if (prev === 'desktop') return 'tablet';
      if (prev === 'tablet') return 'cell';
      return 'desktop';
    });
  }, []);

  // 🟥 CUADRO NEGRO — Canal Chat → Workspace.
  // Envía el payload estructurado al backend y dispara los eventos globales
  // que el Workspace ya escucha (`damyxia-open-internal-module`).
  const executeAction = useCallback(async (payload) => {
    if (!payload || typeof payload !== 'object') {
      throw new Error('executeAction: payload requerido');
    }
    const res = await axios.post(`${API}/workspace/execute-action`, payload, {
      withCredentials: true,
    });
    const data = res?.data || {};
    if (data.success && data.module_id) {
      try {
        window.dispatchEvent(new CustomEvent('damyxia-open-internal-module', {
          detail: {
            moduleId: data.module_id,
            name: data.name,
            detectedType: data.type,
            source: 'chat_execute_action',
          },
        }));
      } catch (_) { /* noop */ }
    }
    return data;
  }, []);

  return (
    <WorkspaceContext.Provider
      value={{ deviceMode, setDeviceMode, handleDeviceSwitch, executeAction }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    // Fallback safe: si el árbol no está envuelto, devolvemos un stub inerte
    // para evitar crashes durante montajes intermedios de rutas lazy.
    return {
      deviceMode: 'desktop',
      setDeviceMode: () => {},
      handleDeviceSwitch: () => {},
      executeAction: async () => ({ success: false, error: 'no-provider' }),
    };
  }
  return ctx;
};

export default WorkspaceContext;
